import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { INVOICE_SOURCE, type InvoiceSource } from "./types";

// Volca reads invoices out of email, so anything that does not arrive there has to get
// there first. What that takes depends on where it came from: telling somebody holding a
// posted invoice to change a setting in a vendor account is no help to them.
const ROUTING_ADVICE: Partial<
  Record<InvoiceSource, { title: string; body: string }>
> = {
  [INVOICE_SOURCE.vendorPortal]: {
    title: "Vendor websites",
    body: "Where the site allows it, change the invoice email to your Volca address so they arrive on their own. Where it does not, download each invoice and send it on.",
  },
  [INVOICE_SOURCE.softwarePortal]: {
    title: "Another software tool",
    body: "Set invoices to email to your Volca address.",
  },
  [INVOICE_SOURCE.paper]: {
    title: "Paper",
    body: "Scan or photograph each invoice, then send it to your Volca address.",
  },
  [INVOICE_SOURCE.textMessage]: {
    title: "Text messages",
    body: "Save the photo or file from the message, then send it on.",
  },
  [INVOICE_SOURCE.fax]: {
    title: "Fax",
    body: "Use a fax service that delivers to email, pointed at your Volca address. Otherwise scan the page and send it on.",
  },
  [INVOICE_SOURCE.other]: {
    title: "Anything else",
    body: "Send a copy to your Volca address, whichever way suits you.",
  },
};

/**
 * Folded into the forwarding step rather than standing as its own: the welcome screen
 * lists every task up front, so the task list cannot grow or shrink with an answer.
 */
export const OtherSourcesSection = ({
  sources,
}: {
  sources: InvoiceSource[];
}) => {
  const advice = sources
    .map((source) => ROUTING_ADVICE[source])
    .filter((entry): entry is { title: string; body: string } =>
      Boolean(entry),
    );
  if (!advice.length) return null;

  return (
    <Card className="w-full gap-0 py-0">
      <CardContent className="p-6">
        <Stack className="w-full" gap={4} align="stretch">
          <Stack className="w-full" align="start" gap={1}>
            <Text variant="title">Invoices that do not arrive by email</Text>
            <Text tone="muted">
              These need a route to your Volca address too. Use the same address
              every time, so nothing is missed.
            </Text>
          </Stack>
          {advice.map(({ title, body }) => (
            <Stack className="w-full" align="start" key={title} gap={1}>
              <Text weight="semibold" variant="caption">
                {title}
              </Text>
              <Text tone="muted">{body}</Text>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
